import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { safeConsole } from '../../utils/productionUtils';

/**
 * React Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now() // Unique ID for this error instance
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    safeConsole.error('Error Boundary Caught Error:', error);
    safeConsole.error('Error Info:', errorInfo);
    
    // Store error details in state
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // TODO: Send error to logging service (e.g., Sentry, LogRocket)
    // this.logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    // Reset error boundary state
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  handleGoHome = () => {
    // Navigate to home page
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      return (
        <div className="min-h-screen bg-[#0F1218] text-[#F9FAFB] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-[#1A1D23] rounded-lg p-6 border border-[#374151]">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-500/10 p-3 rounded-full">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </div>
            
            <h1 className="text-xl font-bold text-center mb-2">
              Oops! Something went wrong
            </h1>
            
            <p className="text-[#9CA3AF] text-center mb-6">
              {this.props.fallbackMessage || 
               "We encountered an unexpected error. Don't worry, this has been logged and we'll look into it."}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex-1 bg-[#374151] hover:bg-[#4B5563] text-[#F9FAFB] px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-sm">
                <summary className="cursor-pointer text-[#9CA3AF] hover:text-[#F9FAFB]">
                  Error Details (Dev Only)
                </summary>
                <div className="mt-2 p-3 bg-[#0F1218] rounded border text-red-400 font-mono text-xs overflow-auto">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  <div>
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap mt-1">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    // Render children normally when there's no error
    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with error boundary
 */
export const withErrorBoundary = (WrappedComponent, fallbackMessage) => {
  const ComponentWithErrorBoundary = (props) => (
    <ErrorBoundary fallbackMessage={fallbackMessage}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  
  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return ComponentWithErrorBoundary;
};

export default ErrorBoundary;